// Info bubble behaviour for the [info] shortcode (.js-info-bubble).
// One bubble open at a time; the panel is clamped so it never overflows
// the viewport horizontally and flips below the trigger when there is no
// room above.

const VIEWPORT_PADDING = 8; // px gap kept between the panel and the viewport edge

let openBubble = null;
let documentController = null;

function getPanel(bubble) {
    return bubble.querySelector('.info-bubble__panel');
}

function getTrigger(bubble) {
    return bubble.querySelector('.info-bubble__trigger');
}

/**
 * Position the open panel relative to its trigger, clamped to the viewport.
 * The panel is absolutely positioned against the .info-bubble wrapper; we
 * measure its natural size, then shift it horizontally so it stays fully
 * on-screen and flip it below the trigger if it would poke above the fold.
 */
function positionPanel(bubble) {
    const panel = getPanel(bubble);
    const trigger = getTrigger(bubble);
    if (!panel || !trigger) return;

    // Reset any previous clamping before measuring the natural placement.
    panel.style.removeProperty('--info-bubble-shift');
    bubble.classList.remove('is-below');

    const viewportWidth = document.documentElement.clientWidth;
    const maxWidth = Math.min(320, viewportWidth - VIEWPORT_PADDING * 2);
    panel.style.setProperty('--info-bubble-max-width', `${maxWidth}px`);

    const panelRect = panel.getBoundingClientRect();

    // Horizontal clamp: shift the centred panel back inside the viewport.
    let shift = 0;
    if (panelRect.left < VIEWPORT_PADDING) {
        shift = VIEWPORT_PADDING - panelRect.left;
    } else if (panelRect.right > viewportWidth - VIEWPORT_PADDING) {
        shift = (viewportWidth - VIEWPORT_PADDING) - panelRect.right;
    }
    if (shift !== 0) {
        panel.style.setProperty('--info-bubble-shift', `${shift}px`);
    }

    // Vertical flip: default placement is above the trigger; drop below
    // when the panel would leave the top of the viewport.
    if (panelRect.top < VIEWPORT_PADDING) {
        bubble.classList.add('is-below');
    }
}

function openInfoBubble(bubble) {
    if (openBubble && openBubble !== bubble) {
        closeInfoBubble(openBubble);
    }

    const panel = getPanel(bubble);
    const trigger = getTrigger(bubble);
    if (!panel || !trigger) return;

    panel.hidden = false;
    trigger.setAttribute('aria-expanded', 'true');
    bubble.classList.add('is-open');
    openBubble = bubble;

    positionPanel(bubble);
}

function closeInfoBubble(bubble) {
    const panel = getPanel(bubble);
    const trigger = getTrigger(bubble);
    if (panel) panel.hidden = true;
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
    bubble.classList.remove('is-open', 'is-below');
    if (openBubble === bubble) openBubble = null;
}

function closeOpenBubble() {
    if (openBubble) closeInfoBubble(openBubble);
}

/**
 * Initialize info bubbles inside a container (defaults to the whole document).
 * Safe to call repeatedly — already-initialized bubbles are skipped, and the
 * document-level listeners (outside click, Escape, resize/scroll reposition)
 * are only attached once.
 *
 * @param {ParentNode} container Scope to search for .js-info-bubble elements.
 */
export function initializeInfoBubbles(container = document) {
    container.querySelectorAll('.js-info-bubble').forEach(bubble => {
        if (bubble.classList.contains('js-info-bubble-init')) return;
        bubble.classList.add('js-info-bubble-init');

        const trigger = getTrigger(bubble);
        if (!trigger) return;

        trigger.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            if (bubble.classList.contains('is-open')) {
                closeInfoBubble(bubble);
            } else {
                openInfoBubble(bubble);
            }
        });
    });

    if (!documentController) {
        documentController = new AbortController();
        const { signal } = documentController;

        document.addEventListener('click', (e) => {
            if (openBubble && !openBubble.contains(e.target)) {
                closeOpenBubble();
            }
        }, { signal });

        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && openBubble) {
                const trigger = getTrigger(openBubble);
                closeOpenBubble();
                if (trigger) trigger.focus();
            }
        }, { signal });

        window.addEventListener('resize', () => {
            if (openBubble) positionPanel(openBubble);
        }, { signal });

        window.addEventListener('scroll', () => {
            if (openBubble) positionPanel(openBubble);
        }, { signal, passive: true });
    }
}

/**
 * Tear down document-level listeners and reset state (Barba page cleanup).
 */
export function destroyInfoBubbles() {
    closeOpenBubble();
    if (documentController) {
        documentController.abort();
        documentController = null;
    }
}
